import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { getAllHall, loadAndGetAllHall, searchHall } from '$logic/hall'
import { searchUser, useSearchedUserByIds } from '$logic/users'
import { getAllPvs, loadAndGetAllPv } from '$logic/pv'
import { getAllWappRoom } from '$logic/wapp-room'
import { t } from '$i18n'
import { getPrivateSpace } from '$logic/private-space'
import { patternMatching } from '$func/text'
import { getRoom } from '$logic/room'
import { useSearchedContacts } from '$logic/contacts/react-hook'
import { useFuse } from '$func/hooks'
import { wappByIdView } from '$logic/wapps/wapps.reducer'
import { roomListSearchView } from './room-list-search.storage'
import {
  calculateRecentUsers,
  deleteFromRecentSearch,
} from './room-list-search.service'

export function useRecentUsers() {
  useEffect(() => {
    calculateRecentUsers()
  }, [])
  return useSelector(state => roomListSearchView(state)?.recentUsers || [])
}

export function useRecentSearchedRooms() {
  const recentSearch = useSelector(
    state => roomListSearchView(state)?.recentSearch || [],
  )

  // NOTE: If room deleted is not reachable, remove from list.
  useEffect(() => {
    recentSearch.forEach(async item => {
      if (item.type === 'ROOM') {
        const room = await getRoom(item.id)
        if (!room) deleteFromRecentSearch(item)
      }
    })
  }, [])

  return recentSearch
}

function useSearchInLocalPvs(query: string) {
  // NOTE: load all pvs to redux
  useAsyncFunction(loadAndGetAllPv, [])
  const allPvs = getAllPvs()
  const { searchedItems, loading } = useSearchedUserByIds(
    query,
    allPvs.map(pv => pv.to),
  )
  const pvs = allPvs.filter(pv => searchedItems.includes(pv.to))
  return {
    roomIds: pvs.map(pv => pv.roomId),
    loading,
    userIds: pvs.map(pv => pv.to),
  }
}

function useSearchInLocalHalls(query: string) {
  const { loading } = useAsyncFunction(loadAndGetAllHall, [])
  const halls = getAllHall()
  const results = useFuse({
    items: halls,
    keys: ['name', 'identifier'],
    query,
  })
  const roomIds = useMemo(() => results.map(({ roomId }) => roomId), [results])
  return {
    roomIds,
    loading,
  }
}

function useSearchInLocalWappRooms(query: string) {
  const wappRooms = getAllWappRoom()
  const wapps = wappRooms.map(({ wappId, roomId }) => ({
    ...wappByIdView(wappId),
    roomId,
  }))
  const results = useFuse({
    items: wapps,
    keys: ['name'],
    query,
  })
  return useMemo(() => results.map(wapp => wapp.roomId), [results])
}

function searchIsPrivateSpace(query: string) {
  return patternMatching(t('chatList.privateSpace'), query)
}

// TODO: Use debounce
export function useSearchedLocalRooms(query: string) {
  const {
    roomIds: pvs = [],
    loading: pvsLoading,
    userIds: pvUserIds = [],
  } = useSearchInLocalPvs(query)
  const { roomIds: halls = [], loading: hallsLoading } = useSearchInLocalHalls(
    query,
  )
  const wappRooms = useSearchInLocalWappRooms(query)

  const roomIds = [
    ...pvs,
    ...halls,
    ...wappRooms,
    ...(searchIsPrivateSpace(query) ? [getPrivateSpace()?.roomId] : []),
  ]
  return { roomIds, loading: pvsLoading || hallsLoading, pvUserIds }
}

// TODO: Use debounce
export function useGlobalSearch(query: string) {
  const {
    value: searchHallResult,
    loading: hallsLoading,
  } = useAsyncFunction(() => searchHall(query), [query])
  const { halls, userHalls } = searchHallResult || { halls: [], userHalls: [] }

  const {
    value: searchUserResult,
    loading: usersLoading,
  } = useAsyncFunction(() => searchUser(query), [query])
  const { users, pvUsers, pvIds } = searchUserResult || {
    users: [],
    pvUsers: [],
    pvIds: [],
  }

  const contacts = useSearchedContacts(query)
  return {
    halls,
    userHalls,
    users,
    pvUsers,
    pvIds,
    contacts,
    loading: hallsLoading || usersLoading,
  }
}
